import { useEffect, useState } from 'react';

import { Button } from '@domcrm/embed-ui';

import { postLeaveOrganization } from '../widget-api/auth-me';
import { ApiError } from '../widget-api/http-client';
import { EmbedMessageBlock } from './embed-message-block';

type Props = {
  open: boolean;
  accessToken: string;
  organizationId: number;
  organizationName: string;
  onLeft: () => void;
  onCancel: () => void;
};

export function LeaveOrganizationDialog({
  open,
  accessToken,
  organizationId,
  organizationName,
  onLeft,
  onCancel,
}: Props) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setBusy(false);
      setErr(null);
    }
  }, [open]);

  if (!open) return null;

  async function submit(): Promise<void> {
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      await postLeaveOrganization(accessToken, organizationId);
      onLeft();
    } catch (e) {
      const msg = e instanceof ApiError ? e.detail : e instanceof Error ? e.message : 'Не удалось выйти из организации';
      setErr(msg);
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/55 p-4"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onCancel();
      }}
    >
      <div
        className="w-full max-w-sm rounded-2xl bg-[var(--bg-elevated)] p-5 shadow-lg"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="leave-org-dialog-title"
        aria-describedby="leave-org-dialog-desc"
      >
        <h2 id="leave-org-dialog-title" className="text-lg font-semibold text-[var(--text)]">
          Выйти из организации
        </h2>
        <p id="leave-org-dialog-desc" className="mt-2 text-sm text-[var(--text-muted)]">
          Вы выйдете из «{organizationName}». Аккаунт сохранится, история записей у организации останется.
        </p>

        {err ? (
          <EmbedMessageBlock className="mt-3 w-full" variant="error" size="field" role="alert" aria-live="assertive">
            {err}
          </EmbedMessageBlock>
        ) : null}

        <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="secondary" className="w-full sm:w-auto" disabled={busy} onClick={onCancel}>
            Отмена
          </Button>
          <Button
            type="button"
            variant="danger"
            className="w-full sm:w-auto"
            disabled={busy}
            onClick={() => void submit()}
          >
            {busy ? 'Выходим…' : 'Выйти'}
          </Button>
        </div>
      </div>
    </div>
  );
}
